import { useQuery } from '@tanstack/react-query';
import { clientApi } from '../api';

// `fetchFlow(uid)` vivía duplicado, crudo, en `OpDetailPanel.jsx` y `SubPanelTratos.jsx` --
// mismo endpoint, dos `useEffect` independientes. `uid` es el id del trato
// (`ClientOperation.id`, UUID), no un id de usuario -- mismo dato en ambos call sites.
export function clientFlowQueryKey(uid) {
    return ['client', 'flow', uid];
}

export async function fetchClientFlow(uid) {
    const d = await clientApi.fetchFlow(uid);
    return d.flow || { nodes: [], edges: [] };
}

// Hook declarativo para el call site simple (un solo `uid`, conocido de entrada) --
// `OpDetailPanel.jsx`, que siempre recibe un `op` real de su único padre.
export function useClientFlow(uid) {
    return useQuery({
        queryKey: clientFlowQueryKey(uid),
        queryFn: () => fetchClientFlow(uid),
        enabled: !!uid,
    });
}

// `SubPanelTratos.jsx` no puede usar el hook de arriba tal cual: en su rama pública
// (`publicUuid`), el `uid` real no se conoce de entrada -- sale de una respuesta anterior
// (`fetchPublicDoc`, ver useClientDocuments.js) dentro de una misma función async con varios
// pasos secuenciales, no de un `useQuery` propio con `enabled` diferido. Pedir el flow a través
// de la cache compartida (mismo `queryKey` que `useClientFlow`) evita una tercera copia del
// fetch y comparte resultado con `OpDetailPanel` si ambos están montados sobre el mismo trato.
export function fetchClientFlowViaCache(queryClient, uid) {
    return queryClient.fetchQuery({ queryKey: clientFlowQueryKey(uid), queryFn: () => fetchClientFlow(uid) });
}

// `saveFlow` se queda como función simple, sin `useMutation` -- el debounce (800ms) y el
// silencio ante error (`.catch(()=>{})`) ya viven en el `useCallback` del call site
// (`OpDetailPanel.jsx`), comportamiento preservado tal cual (02.3-client.md §4: no forzar una
// abstracción de mutación sobre un patrón fire-and-forget que ya funciona).
export function saveClientFlow(uid, { nodes, edges }) {
    return clientApi.saveFlow(uid, { nodes, edges });
}
